import ApiService from '@/services/ApiService';
import User from '@/models/User';
import Role from '@/models/Role';
import Store from '@/store';
import * as mutationTypes from '@/store/mutationTypes';

const USER_TOKEN_KEY = 'user-token';

const UserService = {

    /**
     * Login the User and store the access token to local storage.
     * If we are offline, check the credentials on client
     * and load user without access token
     */
    login(requestData) {
        if (!Store.getters.isOnline) {
            return this.offlineLogin(requestData.pin)
        }

        return new Promise((resolve, reject) => {
            ApiService.post('/user/login', requestData)
                .then(response => {
                    this.setToken(response.data.access_token);
                    this.setUser(response.data.user);

                    let user = new User(response.data.user);

                    const isImpersonate = Store.getters.impersonate
                    if(!isImpersonate) {
                        user.setSession(response.data.session)
                    }

                    resolve(user);
                }).catch(error => {
                    console.log(error.message)
                    reject(error)
                })
        });
    },

    /**
     * Perform offline login
     */
    offlineLogin(pin) {
        return new Promise((resolve, reject) => {
            let loggedInUser = null;
            User.query()
                .with('role')
                .get()
                .forEach(user => {
                    if (bcrypt.compareSync(pin, user.pin)) {
                        loggedInUser = user;
                    }
                });

            if (loggedInUser) {
                // Load User to the store
                this.setUser(loggedInUser.$toJson());

                const isImpersonate = Store.getters.impersonate
                if(!loggedInUser.getSession() && !isImpersonate){
                    loggedInUser.clockIn()
                }

                resolve(loggedInUser);
            } else {
                reject('invalid_pin');
            }
        });
    },

    /**
     * Logout the current User and remove the token from storage.
     */
    logout() {
        if (Store.getters.isOnline) {
            ApiService.post('/user/logout', {});
        }

        Store.commit(mutationTypes.SET_CUR_ORDER, { curOrder: null });
        Store.commit(mutationTypes.SET_CUR_ORDER_ITEM, { curOrderItem: null });

        this.removeToken();
        this.removeUser();
    },

    /**
     * Clock out current User and remove the token from storage.
     */
    clockOut() {
        let curUser = new User(Store.getters.user);

        if (!Store.getters.isOnline) {
            curUser.clockOut();

            this.removeToken();
            this.removeUser();
            return;
        }

        ApiService.post('/user/clockout', {}).then(response => {
            curUser.setSession(response.data.session);
        });

        Store.commit(mutationTypes.SET_CUR_ORDER, { curOrder: null });
        Store.commit(mutationTypes.SET_CUR_ORDER_ITEM, { curOrderItem: null });

        this.removeToken();
        this.removeUser();
    },

    /**
     * Set User
     */
    setUser(user){

        if(user) {
            // Filter user permissions according to the app
            user.role.permissions = user.role.permissions.filter(p => {
                if(!isAllegra) {
                    return p.app !== 'Allegra'
                } else {
                    return !p.app || p.app === 'Allegra'
                }
            })
        }

        // Save User in the store
        Store.commit(mutationTypes.SET_USER, { user: user });
        Store.commit(mutationTypes.SET_TRAINING_MODE, { trainingMode: false });

        // Set User Header
        if(!user){
            ApiService.removeUserHeader();
            return;
        }

        ApiService.setUserHeader();
    },

    /**
     * Get the default route to redirect to after login
     */
    defaultRedirect() {
        const isWeb = Store.getters.device && Store.getters.device.platform === 'web'
        const isProduction = process.env.VUE_APP_ENV === 'production';

        if(isWeb && isProduction) {
            return this.allows('nav_access_admin') ? { name: 'adminDashboard' } : null
        }

        if(isAllegra) {
            return { name: 'allegraOrder' }
        }

        if (this.allows('nav_admin_default')) {
            return { name: 'adminDashboard' }
        } else if (this.allows('nav_tables_default')) {
            return { name: 'tables' }
        } else if (this.allows('nav_order_default')) {
            return { name: 'order' }
        }

        return null
    },

    /**
     * Check if user has a one permission or
     * multiple specified permissions
     *
     * @param permissions
     * @returns {boolean}
     */
    allows(permissions, user = null) {
        if (!Array.isArray(permissions)) {
            permissions = [permissions];
        }

        if(!permissions.length) {
            return true
        }

        if (!user) {
            user = Store.getters.user;
        }

        const userPermissions = user?.role?.permissions || Role.find(user?.role_id)?.permissions
        if (!userPermissions) return false

        const userPermissionNames = userPermissions.map(p => p.name)

        let allowed = true;
        permissions.forEach(p => {
            if (!userPermissionNames.includes(p)) {
                allowed = false;
            }
        });

        return allowed;
    },

    /**
     * Remove User
     */
    removeUser() {
        this.setUser(null);
    },

    /**
     * Get User Token
     */
    getToken() {
        return AppStorage.local().getItem(USER_TOKEN_KEY, null);
    },

    /**
     * Set User Token
     */
    setToken(accessToken) {
        AppStorage.local().setItem(USER_TOKEN_KEY, accessToken, null);
    },

    /**
     * Remove User Token
     */
    removeToken() {
        AppStorage.local().deleteItem(USER_TOKEN_KEY, null);
    }
};

export default UserService;

export { UserService };
